/**
 * El texto del lienzo: una sola tipografía, una sola forma de medirla, una
 * capa de etiquetas y un editor. Lo usan las formas de diagrama y los nodos de
 * texto sueltos.
 *
 * Nació con las formas (ver `docs/plan-formas.md` §D1) y era suyo; hoy es de
 * los dos, porque el texto suelto tenía su propio sistema —markdown
 * rasterizado a textura y editado con CodeMirror— y ese sistema arrastraba
 * exactamente los defectos que éste vino a resolver: borroso al acercar, un
 * salto de posición al confirmar, y una re-rasterización de la tarjeta entera
 * por cada tecla. Un solo camino para todo el texto del lienzo es lo que hace
 * que esos defectos no puedan volver por la puerta de atrás.
 *
 * Lo que este archivo NO sabe es qué está dibujando. La caja donde entra el
 * texto, su color y su alineación se los da quien llama, ya resueltos
 * (`shapeStyle.ts` para una forma, `textStyle.ts` para un nodo de texto). Acá
 * sólo se maqueta: es lo que permite que una forma centre su texto adentro de
 * un rombo y un nodo suelto lo alinee a la izquierda sin que esta capa tenga
 * que preguntar de cuál de los dos se trata.
 *
 * Los elementos son absolutos en píxeles de MUNDO, con `pointer-events:
 * none` para que el click siga llegando al canvas, y viven adentro de un único
 * contenedor que replica la cámara (`translate + scale` desde su esquina). Así
 * panear y hacer zoom es UNA escritura de estilo por frame, no una por
 * etiqueta: con cientos de formas a la vista, mover cada una a mano obligaba
 * al navegador a recalcular estilo y maquetado de todas en cada frame, y era
 * lo que hacía caer los frames. El texto sigue creciendo con el zoom y
 * dibujándose con la nitidez del navegador, sin rasterizar nada.
 *
 * Esta capa va por encima de todo el canvas, así que por sí sola el texto de
 * una tarjeta de abajo se leería a través de la que tiene encima. Para que el
 * apilado se respete, cada etiqueta recibe la silueta de lo opaco que la tapa
 * (`covers`) y la recorta con una máscara. Las etiquetas de contenedores y
 * conectores siguen yendo por encima de todo: son rótulos, no contenido.
 */

import { type TextAlign } from "../model/types.ts";

/** Tipografía del texto del lienzo. Una sola, fija: dos cajas del mismo
 *  diagrama tienen que verse iguales. */
export const CANVAS_TEXT_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
export const CANVAS_TEXT_FONT_SIZE = 16;
export const CANVAS_TEXT_FONT_WEIGHT = 500;
export const CANVAS_TEXT_LINE_HEIGHT = 1.3;

/** Un rectángulo cualquiera, en coordenadas locales del nodo. */
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Todo lo que hace falta para dibujar (o editar) el texto de UN nodo.
 *
 * `box` viene resuelto de afuera: es la caja donde el texto tiene permitido
 * entrar, expresada en coordenadas locales del nodo. En una forma es el
 * rectángulo inscrito en su geometría (de ahí que el texto de un rombo no se
 * escape por las puntas); en un nodo de texto, el rect menos su aire.
 */
export interface CanvasTextItem {
  id: string;
  /** Esquina superior izquierda del nodo, en coordenadas de MUNDO. */
  x: number;
  y: number;
  /** Tamaño del nodo en px de mundo. */
  width: number;
  height: number;
  box: Box;
  /** Color del texto, ya resuelto (por contraste, o fijo). */
  color: string;
  align: TextAlign;
  /** Vertical: una forma centra su texto; un nodo de texto empieza arriba. */
  valign: "center" | "top";
  text: string;
  /** Se oculta mientras se edita: ahí manda el editor. */
  hidden: boolean;
  /** Lo opaco que está más arriba en el apilado y pisa este nodo. */
  covers?: TextCover[];
}

/**
 * Una silueta opaca que tapa parte de un texto, en coordenadas de MUNDO. Con
 * tres figuras alcanza para todo lo que se dibuja: el rect (con su radio) cubre
 * tarjetas, fondos de texto, rectángulos y pastillas.
 */
export interface TextCover {
  kind: "rect" | "ellipse" | "diamond";
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
}

/** Un número corto para el SVG: medio píxel de mundo no se ve. */
const n = (v: number): string => String(Math.round(v * 10) / 10);

/**
 * La máscara que esconde las partes tapadas de una etiqueta: un SVG blanco con
 * las siluetas en negro, aplicado por luminancia. Se usa una máscara y no un
 * `clip-path` porque dos siluetas que se cruzan tienen que sumar, y en un
 * `path` con agujeros la intersección de dos agujeros vuelve a ser visible.
 *
 * Coordenadas locales del elemento —su esquina es la del nodo y se escala con
 * él—, con un margen alrededor para el texto que desborda la caja.
 */
function coverMask(item: CanvasTextItem, covers: TextCover[]): CoverMask {
  const pad = Math.max(item.width, item.height);
  const w = item.width + 2 * pad;
  const h = item.height + 2 * pad;
  let shapes = "";
  for (const c of covers) {
    const x = c.x - item.x;
    const y = c.y - item.y;
    if (c.kind === "ellipse") {
      shapes += `<ellipse cx="${n(x + c.width / 2)}" cy="${n(y + c.height / 2)}" rx="${n(c.width / 2)}" ry="${n(c.height / 2)}"/>`;
    } else if (c.kind === "diamond") {
      const pts = [x + c.width / 2, y, x + c.width, y + c.height / 2, x + c.width / 2, y + c.height, x, y + c.height / 2];
      shapes += `<polygon points="${pts.map(n).join(" ")}"/>`;
    } else {
      shapes += `<rect x="${n(x)}" y="${n(y)}" width="${n(c.width)}" height="${n(c.height)}" rx="${n(c.radius)}"/>`;
    }
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${n(w)}" height="${n(h)}" viewBox="${n(-pad)} ${n(-pad)} ${n(w)} ${n(h)}">` +
    `<rect x="${n(-pad)}" y="${n(-pad)}" width="${n(w)}" height="${n(h)}" fill="#fff"/>` +
    `<g fill="#000">${shapes}</g></svg>`;
  return {
    image: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    size: `${n(w)}px ${n(h)}px`,
    position: `${n(-pad)}px ${n(-pad)}px`,
  };
}

interface CoverMask {
  image: string;
  size: string;
  position: string;
}

function applyMask(el: HTMLElement, mask: CoverMask | null): void {
  const st = el.style;
  if (!mask) {
    st.removeProperty("mask-image");
    return;
  }
  st.setProperty("mask-image", mask.image);
  st.setProperty("mask-mode", "luminance");
  st.setProperty("mask-repeat", "no-repeat");
  st.setProperty("mask-origin", "border-box");
  st.setProperty("mask-clip", "no-clip");
  st.setProperty("mask-size", mask.size);
  st.setProperty("mask-position", mask.position);
}

/** Lo último escrito al DOM de una etiqueta, para no tocarlo en cada frame. */
interface LabelEl {
  el: HTMLElement;
  text: string;
  /** Firma de todo lo que define su caja, su color y su alineación. */
  style: string;
  /** Esquina del nodo, en px de mundo. */
  left: number;
  top: number;
  /** Firma de las siluetas que la tapan, relativas al nodo. */
  mask: string;
}

/** La firma de lo que define cómo se maqueta un ítem (todo menos su posición). */
function layoutKey(item: CanvasTextItem): string {
  return [
    item.width,
    item.height,
    item.box.x,
    item.box.y,
    item.color,
    item.align,
    item.valign,
  ].join("|");
}

/**
 * La capa de texto: un elemento por nodo con texto, sobre el canvas.
 *
 * Se instancia UNA sola vez y la comparten formas y nodos de texto: son la
 * misma clase de cosa (una caja con texto plano adentro) y mezclarlos en la
 * misma capa evita que dos capas hermanas compitan por el orden de apilado.
 */
export class CanvasTextLabels {
  readonly root: HTMLElement;
  /** El contenedor que sigue a la cámara; las etiquetas cuelgan de él. El
   *  editor no: va en `root`, en px de pantalla. */
  private world: HTMLElement;
  private els = new Map<string, LabelEl>();
  private lastCamera = "";

  constructor() {
    ensureCanvasTextStyle();
    this.root = document.createElement("div");
    this.root.className = "canvas-text-labels";
    this.world = document.createElement("div");
    this.world.className = "canvas-text-world";
    this.root.appendChild(this.world);
  }

  /** Crea, actualiza y descarta etiquetas para que reflejen el estado actual. */
  sync(
    items: CanvasTextItem[],
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    zoom: number,
  ): void {
    const live = new Set<string>();

    // La cámara entera en una sola escritura: screen = world * zoom + origen.
    const o = worldToScreen(0, 0);
    const camera = `translate(${o.x}px, ${o.y}px) scale(${zoom})`;
    if (camera !== this.lastCamera) {
      this.lastCamera = camera;
      this.world.style.transform = camera;
    }

    for (const item of items) {
      // Un nodo sin texto no necesita elemento. El editor, cuando se abre,
      // dibuja el suyo propio.
      if (item.hidden || item.text.length === 0) continue;
      live.add(item.id);

      let entry = this.els.get(item.id);
      if (!entry) {
        const el = document.createElement("div");
        // `canvas-text` trae la tipografía y la envoltura, las mismas con las
        // que se midió el alto y con las que escribe el editor; `canvas-text-box`
        // el maquetado en columna; `canvas-text-label` el anclaje absoluto.
        el.className = "canvas-text canvas-text-box canvas-text-label";
        entry = { el, text: " ", style: "", left: NaN, top: NaN, mask: "" };
        this.els.set(item.id, entry);
        this.world.appendChild(el);
      }
      const el = entry.el;

      if (entry.text !== item.text) {
        entry.text = item.text;
        // `renderableText` y no el texto crudo: la etiqueta tiene que ocupar
        // exactamente el alto que se midió y que ocupa el editor.
        el.textContent = renderableText(item.text);
      }

      // La caja se expresa como padding sobre el rect completo: así el elemento
      // cubre exactamente el nodo y el texto queda dentro de la parte que le
      // corresponde (crítico en rombo y elipse, donde las puntas están vacías).
      const style = layoutKey(item);
      if (entry.style !== style) {
        entry.style = style;
        el.style.width = `${item.width}px`;
        el.style.height = `${item.height}px`;
        el.style.padding = `${item.box.y}px ${item.box.x}px`;
        el.style.color = item.color;
        el.style.textAlign = item.align;
        el.style.justifyContent = item.valign === "center" ? "center" : "flex-start";
      }

      // Relativa al nodo: mover un grupo de tarjetas juntas no rehace la máscara.
      const covers = item.covers ?? [];
      const maskKey =
        covers.length === 0
          ? ""
          : `${item.width}|${item.height}|` +
            covers
              .map((c) => `${c.kind},${c.x - item.x},${c.y - item.y},${c.width},${c.height},${c.radius}`)
              .join(";");
      if (entry.mask !== maskKey) {
        entry.mask = maskKey;
        applyMask(el, covers.length === 0 ? null : coverMask(item, covers));
      }

      // Sólo cambia cuando el NODO se mueve: la cámara ya la aplica `world`.
      if (item.x !== entry.left || item.y !== entry.top) {
        entry.left = item.x;
        entry.top = item.y;
        el.style.left = `${item.x}px`;
        el.style.top = `${item.y}px`;
      }
    }

    for (const [id, entry] of this.els) {
      if (live.has(id)) continue;
      entry.el.remove();
      this.els.delete(id);
    }
  }

  destroy(): void {
    for (const entry of this.els.values()) entry.el.remove();
    this.els.clear();
    this.root.remove();
  }
}

/**
 * El textarea con el que se escribe adentro de un nodo.
 *
 * Vive en este archivo y no en uno propio porque la etiqueta y él son **dos
 * estados de la misma caja**: se turnan sobre el mismo rectángulo, con la misma
 * tipografía y la misma caja de texto, y se montan en el mismo contenedor
 * posicionado. Tenerlos separados es lo que hacía que el texto saltara de lugar
 * al confirmar (`docs/plan-formas.md` §1.2): eran dos maquetados distintos que
 * había que acordarse de sincronizar. Acá no hay nada que sincronizar.
 *
 * No es CodeMirror: el texto del lienzo es texto plano, no markdown, y no
 * necesita ni historial propio (deshacer es el del canvas) ni keymap ni menú de
 * barra. `Enter` es una línea nueva; `Escape`, `⌘Enter` y el clic afuera
 * confirman.
 *
 * No sabe nada del store: avisa cada cambio por callback, igual que
 * `InlineInput`.
 */
export interface CanvasTextEditorHandlers {
  /** El texto cambió. Se llama en cada tecla. */
  onChange: (id: string, text: string) => void;
  /** Se cerró el editor. El texto ya se reportó por `onChange`. */
  onClose: (id: string) => void;
}

export class CanvasTextEditor {
  private el: HTMLTextAreaElement | null = null;
  private id: string | null = null;
  private handlers: CanvasTextEditorHandlers | null = null;
  /** Firma de lo último escrito al DOM, para no tocarlo en cada frame. */
  private lastKey = "";

  /** `host` es la capa de las etiquetas: el mismo contenedor posicionado que
   *  ellas, así el editor cae en el píxel exacto donde estaba el texto. */
  constructor(private host: HTMLElement) {
    ensureCanvasTextStyle();
  }

  get editingId(): string | null {
    return this.id;
  }

  start(id: string, text: string, handlers: CanvasTextEditorHandlers): void {
    this.close();

    const el = document.createElement("textarea");
    el.className = "canvas-text canvas-text-input";
    el.value = text;
    el.spellcheck = false;
    this.el = el;
    this.id = id;
    this.handlers = handlers;
    this.lastKey = "";

    el.addEventListener("keydown", (e) => {
      // El canvas escucha teclas en `window`: sin esto, sus atajos se comerían
      // lo que se está escribiendo.
      e.stopPropagation();
      if (e.key === "Escape" || (e.key === "Enter" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        this.close();
      }
      // `Enter` a secas cae al comportamiento normal del textarea: línea nueva.
    });
    el.addEventListener("input", () => {
      this.handlers?.onChange(id, el.value);
    });
    el.addEventListener("blur", () => this.close());
    // El clic adentro del editor no es un clic en el canvas.
    el.addEventListener("pointerdown", (e) => e.stopPropagation());

    this.host.appendChild(el);
    el.focus();
    // Igual que al renombrar un grupo: se entra con todo seleccionado, así
    // reescribir una caja es escribir, sin borrar antes.
    el.select();
  }

  /**
   * Mantiene el editor sobre el nodo mientras se hace pan, zoom o crece la
   * caja. Recibe el MISMO ítem con el que se habría dibujado la etiqueta, que
   * es lo que garantiza que ocupe su lugar exacto.
   */
  sync(item: CanvasTextItem, screen: { x: number; y: number }, zoom: number): void {
    const el = this.el;
    if (!el) return;

    const left = Math.round(screen.x);
    const top = Math.round(screen.y);
    const key = `${layoutKey(item)}|${left}|${top}|${zoom}|${el.value}`;
    if (key === this.lastKey) return;
    this.lastKey = key;

    // Un textarea no se puede centrar verticalmente por CSS, así que el
    // centrado se calcula: se mide el texto con la misma función que usa la
    // etiqueta y se baja el contenido esa mitad. De ahí sale que entrar y salir
    // de edición no mueva el texto ni un píxel. Con `valign: "top"` no hay nada
    // que calcular: el aire de arriba es el de la caja y nada más.
    const padTop =
      item.valign === "center"
        ? item.box.y +
          Math.max(0, (item.box.height - measureTextHeight(el.value, item.box.width)) / 2)
        : item.box.y;

    el.style.width = `${item.width}px`;
    el.style.height = `${item.height}px`;
    el.style.padding = `${padTop}px ${item.box.x}px 0`;
    el.style.color = item.color;
    el.style.caretColor = item.color;
    el.style.textAlign = item.align;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.transform = `scale(${zoom})`;
  }

  /** Cierra el editor. Lo que se escribió ya se guardó en cada `onChange`. */
  close(): void {
    const el = this.el;
    const id = this.id;
    const handlers = this.handlers;
    this.el = null;
    this.id = null;
    this.handlers = null;
    if (!el || !id) return;
    el.remove();
    handlers?.onClose(id);
  }
}

// ---- la clase compartida ---------------------------------------------------

/**
 * Todo lo que define cómo se ve y cómo se envuelve el texto del lienzo, en UNA
 * sola regla, inyectada desde acá y no escrita en `styles.css`.
 *
 * Es deliberado: la etiqueta que se ve, el textarea con el que se escribe y el
 * nodo con el que se mide el alto tienen que maquetar idéntico, y la única
 * forma de garantizarlo es que sea literalmente la misma regla y no tres copias
 * que hay que acordarse de sincronizar. Que fueran dos copias distintas es lo
 * que hacía saltar el texto al confirmar (ver `docs/plan-formas.md` §1.2).
 *
 * Lo que NO va acá es lo que cambia por nodo: tamaño, padding (la caja de
 * texto), color, alineación y la posición en pantalla.
 */
const CANVAS_TEXT_CSS = `
  .canvas-text {
    box-sizing: border-box;
    /* La alineación horizontal es text-align y NO align-items: en una columna
       flex, centrar el eje cruzado achica el item a su contenido y el texto
       deja de envolver al ancho de la caja, que es justo lo que hay que medir. */
    text-align: left;
    font-family: ${CANVAS_TEXT_FONT_FAMILY};
    font-size: ${CANVAS_TEXT_FONT_SIZE}px;
    font-weight: ${CANVAS_TEXT_FONT_WEIGHT};
    line-height: ${CANVAS_TEXT_LINE_HEIGHT};
    white-space: pre-wrap;
    word-break: break-word;
    overflow: hidden;
  }

  /* Columna flex: el centrado vertical de una forma sale de su
     \`justify-content\`, que pone la etiqueta. El textarea NO puede usarlo (no
     hay forma de centrar verticalmente el contenido de un textarea), así que
     ahí el mismo efecto se consigue con un padding-top calculado — ver
     \`CanvasTextEditor\`. Las dos rutas miden el texto con la misma función, así
     que dan el mismo píxel. */
  .canvas-text-box {
    display: flex;
    flex-direction: column;
  }

  /* El textarea con el que se escribe: invisible salvo por su texto y su
     cursor, y exactamente sobre la etiqueta que reemplaza. */
  .canvas-text-input {
    display: block;
    position: absolute;
    transform-origin: 0 0;
    pointer-events: auto;
    margin: 0;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
  }
`;

let styleInjected = false;

/** Deja `.canvas-text` disponible. Idempotente; la llama quien vaya a usarla. */
export function ensureCanvasTextStyle(): void {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement("style");
  style.textContent = CANVAS_TEXT_CSS;
  document.head.appendChild(style);
}

/**
 * El nodo con el que se mide, fuera de la vista.
 *
 * Es propio y no el de `snapshot.ts`: el texto del lienzo ya no pasa por el
 * pipeline de rasterizado, y depender de su escenario lo volvería a atar a él.
 */
let stage: HTMLDivElement | null = null;

function measureNode(): HTMLDivElement {
  if (!stage) {
    ensureCanvasTextStyle();
    stage = document.createElement("div");
    stage.style.cssText =
      "position:fixed;left:-10000px;top:0;pointer-events:none;visibility:hidden";
    const node = document.createElement("div");
    // La misma clase que la etiqueta y que el editor: se mide lo que se ve.
    node.className = "canvas-text";
    stage.appendChild(node);
    document.body.appendChild(stage);
  }
  return stage.firstElementChild as HTMLDivElement;
}

/**
 * El texto tal como hay que ponerlo en el DOM para que ocupe lo que debe.
 *
 * Un `\n` final no tiene altura por sí solo: el navegador no le da una línea, y
 * sin embargo un textarea SÍ muestra esa línea (es donde está el cursor recién
 * apretado el Enter). El marcador de ancho cero se la da.
 *
 * Lo usan la medición Y la etiqueta, y ése es el punto: si sólo lo usara una,
 * al terminar el texto en Enter la etiqueta se centraría media línea más arriba
 * que el editor y el texto saltaría justo al confirmar — el defecto que este
 * componente vino a sacar.
 */
export function renderableText(text: string): string {
  if (text === "") return " ";
  return /\s$/.test(text) ? `${text}\u200b` : text;
}

/** El alto de una línea con esta tipografía. Es el piso de toda medición: una
 *  caja vacía sigue ocupando una línea y no se desarma al borrar todo. */
export const CANVAS_TEXT_LINE_PX = Math.ceil(
  CANVAS_TEXT_FONT_SIZE * CANVAS_TEXT_LINE_HEIGHT,
);

/**
 * Alto que ocupa un texto envuelto a un ancho dado. Un texto vacío ocupa una
 * línea.
 */
export function measureTextHeight(text: string, width: number): number {
  if (width <= 0) return CANVAS_TEXT_LINE_PX;
  const node = measureNode();
  // El nodo lo comparten las dos mediciones, así que cada una fija TODO lo que
  // la otra toca: acá el ancho manda y el bloque es de bloque.
  node.style.display = "block";
  node.style.width = `${width}px`;
  node.style.maxWidth = "";
  node.textContent = renderableText(text);
  return Math.max(CANVAS_TEXT_LINE_PX, Math.ceil(node.scrollHeight));
}

/**
 * Tamaño natural de un texto: el que ocuparía sin envolverlo, salvo que pase de
 * `maxWidth`, donde empieza a envolver. Es la medida del texto auto-ancho (ver
 * `TextData.autoWidth`).
 */
export function measureTextBlock(
  text: string,
  maxWidth: number,
): { width: number; height: number } {
  const node = measureNode();
  // `inline-block` no es un detalle: un bloque con `width: auto` ocupa el ancho
  // de su padre y `scrollWidth` mediría eso y no el texto. Encogido al
  // contenido, la línea más larga es el ancho.
  node.style.display = "inline-block";
  node.style.width = "auto";
  node.style.maxWidth = `${maxWidth}px`;
  node.textContent = renderableText(text);
  // Un par de píxeles de más: sin ellos el cursor al final de la línea más
  // larga fuerza una envoltura sobre el último glifo.
  const width = Math.min(maxWidth, Math.ceil(node.scrollWidth) + 2);
  return { width, height: Math.max(CANVAS_TEXT_LINE_PX, Math.ceil(node.scrollHeight)) };
}
